/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Collapse, Form, Input, Row, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import FormSectionHeader from '../../../UI/FormSectionHeader';
import CustomInputNumber from '../../Fields/CustomInputNumber';
import EntryPointSelect from '../../Fields/EntryPointSelect';

// Misc imports
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';
import openConsoleAlertMessage from '../../../../utils/misc/openConsoleAlertMessage';

const NOTIFICATION_LEVEL = {
  ERROR: 'Error',
  INFO: 'Info',
  SUCCESS: 'Success',
  WARN: 'Warn'
};

const FORM_CONFIGURATION_LAYOUT_PATH = ['settings', 'layout', 'microsoftWebChat', 'configuration'];

const BotDeploymentsWebchatNotificationsForm = ({
  form,
  formData = {},
  userRoles = {},
  isCreateMode = false,
  type,
  hasErrors=0,
  shouldMarkAsErroneous
}) => {
  const baseClassName = 'webchat-settings__notifications-form';
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);

  useEffect(() => {
    if (!hasErrors) {
      shouldMarkAsErroneous(false);
      return;
    }

    const notifications = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type]);

    const hasError = notifications?.some((notificationObj, idx) => {
      if (form.getFieldError([...FORM_CONFIGURATION_LAYOUT_PATH, type, idx, 'text']).length > 0) {
        return true;
      }
      return false;
    });

    shouldMarkAsErroneous(hasError);
  }, [hasErrors, form]);

  // Watch for changes in enableButtons fields to manage button lists
  const handleEnableButtonsChange = (checked, notificationIndex, notificationType) => {
    const notifications = form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, notificationType]);

    if (!checked && notifications[notificationIndex].buttons?.length > 0) {
      // Clear buttons when disabling
      const updatedNotifications = [...notifications];
      updatedNotifications[notificationIndex].buttons = undefined;
      form.setFieldsValue({ [notificationType]: updatedNotifications });
    }
  };

  return (
    <Fragment>
      {/* Start up notifications */}
      <Row gutter={formProps.rowGutter}>
        <Col xs={24}>
          <Typography.Text>
            {type === 'startUpNotifications' ? t('Start Up Notifications') : t('Idle Notifications')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {type === 'startUpNotifications' ?t(`Add notifications to be displayed upon deployment's startup`) : t(`Add notifications to be displayed when the chat turns idle`)}
          </Typography.Paragraph>
        </Col>
        <Col xs={24}>
          <Form.List
            name={[...FORM_CONFIGURATION_LAYOUT_PATH, type]}
          >
            {(notificationsFields, { add: addNotification, remove: removeNotification }, { errors: notificationsErrors }) => (
              <Fragment>
                {
                  notificationsFields.length >= 1
                    ? notificationsFields.map(({ key, name: notificationName, ...restField })=> <Card
                      key={[...FORM_CONFIGURATION_LAYOUT_PATH, type, notificationName].join('-')}
                      size="small"
                      className="bg-layout-gray shadow-none mb-3"
                    >
                      {/* Start Up Notification Text */}
                      <Form.Item
                        label={t('Text')}
                        name={[notificationName, 'text']}
                        rules={[
                          { required: true, whitespace: true, message: 'Missing start up notification text' }
                        ]}
                      >
                        <Input.TextArea
                          disabled={!hasEditAccess}
                          placeholder={t('Notification copy')}
                          autoSize={{ minRows: 3, maxRows: 32 }}
                        />
                      </Form.Item>
                      {/* ./ Start Up Notification Text */}

                      {
                        type === 'idleNotifications'
                          ? <><Row gutter={16}>
                            <Col xs={24} sm={12}>
                              {/* Idle Notification inactive for */}
                              <Form.Item
                                label={t('Notification Delay (seconds)')}
                                name={[notificationName, 'triggerWhenInactiveFor']}
                                initialValue={60}
                              >
                                <CustomInputNumber step={10} buttonClassName="bg-skeleton"/>
                              </Form.Item>
                              {/* ./Idle Notification inactive for */}
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                label={t('Play sound')}
                                valuePropName="checked"
                                name={[notificationName, 'hasSound']}
                              >
                                <Checkbox />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Collapse ghost className={`${baseClassName}__collapse mb-5`}>
                            <Collapse.Panel
                              header={<FormSectionHeader className="m-0" title={t('Advanced settings')}/>}
                              showArrow={false}
                              key="1"
                            >
                              <Row gutter={16}>
                                <Col
                                  xs={24}
                                  sm={type === 'startUpNotifications' ? 24 : 12}
                                >
                                  {/* Start Up Level */}
                                  <Form.Item
                                    className="mb-0"
                                    label={t('Level')}
                                    name={[notificationName, 'level']}
                                    initialValue="INFO"
                                  >
                                    <Select
                                      placeholder={t('Select notification level')}
                                      showSearch={false}
                                      disabled
                                      getPopupContainer={triggerNode => triggerNode.parentElement}
                                    >
                                      {
                                        Object.entries(NOTIFICATION_LEVEL).map(([key, value]) => {
                                          return <Select.Option key={key} value={key}>
                                            {value}
                                          </Select.Option>;
                                        })
                                      }
                                    </Select>
                                  </Form.Item>
                                  {/* ./Start Up Level */}
                                </Col>

                                <Col xs={24} sm={12}>
                                  <Form.Item
                                    className="mb-0"
                                    shouldUpdate
                                  >
                                    {
                                      () => (
                                        <Form.Item
                                          className="mb-0"
                                          label={t('Trigger flow')}
                                          name={[notificationName, 'triggerConversationNodeId']}
                                        >
                                          {
                                            hasEditAccess
                                              ? <EntryPointSelect
                                                allowClear
                                                placeholder={t('Select flow to be triggered')}
                                              />
                                              : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, notificationName, 'triggerConversationNodeId'])}</Typography.Text>
                                          }
                                        </Form.Item>
                                      )
                                    }
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Collapse.Panel>
                          </Collapse>
                          </>
                          : null
                      }

                      {/* Enable Buttons Checkbox */}
                      <Form.Item
                        {...restField}
                        className="mb-2"
                        name={[notificationName, 'enableButtons']}
                        valuePropName="checked"
                        initialValue={form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, notificationName, 'buttons'])?.length > 0}
                      >
                        <Checkbox onChange={(e) => handleEnableButtonsChange(e.target.checked, notificationName, type)}>
                          {t('Enable Buttons')}
                        </Checkbox>
                      </Form.Item>
                      {/* ./ Enable Buttons Checkbox */}

                      {/* Buttons List - Only shown when enableButtons is checked */}
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => {
                          return prevValues?.settings?.layout?.microsoftWebChat?.configuration?.[type]?.[notificationName]?.enableButtons
                             !== currentValues?.settings?.layout?.microsoftWebChat?.configuration?.[type]?.[notificationName]?.enableButtons;
                        }}
                      >
                        {({ getFieldValue }) => {
                          const enableButtons = (getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, notificationName, 'enableButtons'])
                                    || getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, notificationName, 'buttons'])?.length > 0);
                          return enableButtons ? (

                            <Form.List name={[notificationName, 'buttons']}>
                              {(buttonFields, { add: addButton, remove: removeButton }) => (
                                <>
                                  {buttonFields.map((buttonField, index) => (
                                    <Card
                                      size="small"
                                      className="bg-layout-light shadow-none position-relative mb-4">
                                      <Row gutter={formProps.rowGutter}>
                                        <Col span={24}>
                                          {/* Button Title */}
                                          <Form.Item
                                            {...buttonField}
                                            label={t('Button Name')}
                                            name={[buttonField.name, 'title']}
                                            rules={[{ required: true, message: t('Missing name') }]}
                                          >
                                            {
                                              hasEditAccess
                                                ? <Input allowClear placeholder={t('Enter the button name')} />
                                                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, buttonField.name, 'title'])}</Typography.Text>
                                            }
                                          </Form.Item>

                                          {/* Conversation Node ID */}
                                          <Form.Item
                                            className="mb-0"
                                            shouldUpdate
                                          >
                                            {
                                              () => (
                                                <Form.Item
                                                  {...buttonField}
                                                  name={[buttonField.name, 'conversationNodeId']}
                                                  rules={[{ required: true, message: 'Missing node ID' }]}
                                                  label={t('Flow')}
                                                >
                                                  {
                                                    hasEditAccess
                                                      ? <EntryPointSelect
                                                        allowClear
                                                        placeholder={t('Select flow to be triggered')}
                                                      />
                                                      : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_LAYOUT_PATH, type, buttonField.key, 'conversationNodeId'])}</Typography.Text>
                                                  }
                                                </Form.Item>
                                              )
                                            }
                                          </Form.Item>

                                          <Button
                                            type="text"
                                            onClick={() => removeButton(buttonField.name)}
                                            shape="circle"
                                            icon={<DeleteOutlined />}
                                            className="float-right bordered border-radius-base"
                                          />
                                        </Col>
                                      </Row>

                                    </Card>
                                  ))}

                                  {buttonFields.length < 3 && (
                                    <Form.Item>
                                      <Button
                                        type="dashed"
                                        onClick={() => {
                                          if (buttonFields.length < 3) {
                                            addButton();
                                          } else {
                                            openConsoleAlertMessage({ type: 'warning', message: t('Maximum 3 buttons allowed per notification') });
                                          }
                                        }}
                                        icon={<PlusOutlined />}
                                      >
                                        {t('Add Button')} ({buttonFields.length}/3)
                                      </Button>
                                    </Form.Item>
                                  )}
                                </>
                              )}
                            </Form.List>
                          ) : null;
                        }}
                      </Form.Item>
                      {/* ./ Start Up Buttons */}
                      <Row justify="end">
                        <Col >
                          <Button
                            type="text"
                            onClick={() => removeNotification(notificationName)}
                            shape="circle"
                            icon={<DeleteOutlined />}
                            className="float-right bordered border-radius-base"
                          />

                        </Col>
                      </Row>
                    </Card>
                    )
                    : null
                }

                <Button
                  type="dashed"
                  size="large"
                  style={{ maxWidth: 464 }}
                  block
                  onClick={() => {
                    addNotification();
                  }}
                  className="text-secondary mb-3"
                  icon={<PlusOutlined />}
                  disabled={!hasEditAccess}
                >
                  {type === 'startUpNotifications' ? t('Add Notification') : t('Add idle notifications')}
                </Button>
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>
      {/* ./Notifications */}

    </Fragment>
  );
};

BotDeploymentsWebchatNotificationsForm.propTypes = {
  form: PropTypes.object,
  formData: PropTypes.object,
  userRoles: PropTypes.object,
  isCreateMode: PropTypes.bool,
  type: PropTypes.oneOf(['startUpNotifications', 'idleNotifications']),
  hasErrors: PropTypes.number,
  shouldMarkAsErroneous: PropTypes.func
};

export default BotDeploymentsWebchatNotificationsForm;
